import type * as React from "react";
import { XIcon } from "lucide-react";

import { cx } from "../../cx";
import styles from "./alert.module.css";

export type AlertVariant =
  | "default"
  | "destructive"
  | "info"
  | "warning"
  | "success"
  | "brand";
export type AlertPlacement = "standard" | "compact" | "banner";

type AlertProps = React.ComponentProps<"div"> & {
  variant?: AlertVariant;
  placement?: AlertPlacement;
  onDismiss?: () => void;
  dismissLabel?: string;
};

function Alert({
  className,
  variant = "default",
  placement = "standard",
  onDismiss,
  dismissLabel = "Dismiss",
  children,
  ...props
}: AlertProps) {
  return (
    <div
      data-tui=""
      data-slot="alert"
      data-variant={variant}
      data-placement={placement}
      data-dismissible={onDismiss ? "" : undefined}
      role="alert"
      className={cx(styles.alert, className)}
      {...props}
    >
      {children}
      {onDismiss ? (
        <button
          type="button"
          data-slot="alert-dismiss"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className={styles.dismiss}
        >
          <XIcon strokeWidth={1.75} />
        </button>
      ) : null}
    </div>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cx(styles.title, className)}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cx(styles.description, className)}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle };
